import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import {
  DetailsEmptyIllustration,
  LocationEmptyIllustration,
  MetricsEmptyIllustration,
  PeopleEmptyIllustration,
  SimilarEmptyIllustration,
} from "./illustrations/sidebarEmptyIllustrations";
import { SIDEBAR } from "./sidebarVisual";

export const SIDEBAR_EMPTY = {
  metrics: {
    title: "No metrics available",
    description: "We don’t have enough data yet to show company metrics for this profile.",
    Illustration: MetricsEmptyIllustration,
  },
  details: {
    title: "No details available",
    description: "Classification information isn’t available for this company.",
    Illustration: DetailsEmptyIllustration,
  },
  location: {
    title: "No location available",
    description: "We couldn’t find a primary address or map location for this company.",
    Illustration: LocationEmptyIllustration,
  },
  people: {
    title: "No people listed",
    description: "There aren’t any people profiles associated with this company yet.",
    Illustration: PeopleEmptyIllustration,
  },
  similar: {
    title: "No similar companies",
    description: "We couldn’t find comparable companies with enough information to show here.",
    Illustration: SimilarEmptyIllustration,
  },
  aiEnrichment: {
    title: "No AI enrichment history yet",
    description: "Run an action to generate enrichment results for this company.",
    Illustration: MetricsEmptyIllustration,
  },
  chart: {
    title: "No chart data",
    description: "This estimate range isn’t available for this company.",
    Illustration: MetricsEmptyIllustration,
  },
};

/**
 * Compact empty state for V2 Company Details sidebar tabs.
 * Centered horizontally and vertically in the available panel space.
 */
export default function CompanySidebarEmptyState({
  variant = "details",
  title,
  description,
  testId = "company-sidebar-empty-state",
  compact = false,
  actionLabel,
  onAction,
}) {
  const config = SIDEBAR_EMPTY[variant] || SIDEBAR_EMPTY.details;
  const Illustration = config.Illustration;
  const resolvedTitle = title || config.title;
  const resolvedDescription = description || config.description;

  return (
    <Box
      data-testid={testId}
      sx={{
        width: "100%",
        flex: compact ? "0 0 auto" : 1,
        alignSelf: "stretch",
        minHeight: compact ? 120 : 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: compact ? 1.5 : 3,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: compact ? 280 : 320,
        }}
      >
        <Box aria-hidden sx={{ mb: compact ? 1 : SIDEBAR.gap.listItem, lineHeight: 0 }}>
          <Illustration size={compact ? 56 : 96} />
        </Box>
        <Typography
          component="h3"
          sx={{
            m: 0,
            mb: 0.65,
            fontSize: compact ? 14 : 15,
            fontWeight: 700,
            letterSpacing: "-0.015em",
            lineHeight: 1.25,
            color: "text.primary",
          }}
        >
          {resolvedTitle}
        </Typography>
        <Typography
          sx={{
            m: 0,
            ...SIDEBAR.type.bodySecondary,
            fontSize: 12.5,
            maxWidth: 280,
          }}
        >
          {resolvedDescription}
        </Typography>
        {actionLabel && typeof onAction === "function" ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onAction}
            data-testid={`${testId}-action`}
            sx={{
              mt: 1.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.5,
              borderRadius: "8px",
            }}
          >
            {actionLabel}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

CompanySidebarEmptyState.propTypes = {
  variant: PropTypes.oneOf(Object.keys(SIDEBAR_EMPTY)),
  title: PropTypes.string,
  description: PropTypes.string,
  testId: PropTypes.string,
  compact: PropTypes.bool,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
};
